import { ref, onMounted, nextTick } from 'vue';
import { useMutationObserver } from '@vueuse/core';

export const MAIN_CONTAINER_SELECTOR = '#container.main[role="main"]';
export const MAIN_CONTAINER_Z_INDEX_INITIAL = '1';
export const MAIN_CONTAINER_Z_INDEX_OVERLAP = '3';

// Modal/Dialog selectors that should trigger z-index adjustments
export const MODAL_SELECTORS = [
  '.a-dialog', // library dialog selector
  '.cms-modal', // CmsModal component
  '.form-modal-v2', // FormModalV2 component
  '.contact-modal', // StaffContactModal component
  '.lightbox', // Lightbox component
] as const;

// Composable fixes the issue described in the https://apptegy.atlassian.net/browse/SO-1040
// If dialogs are opened, the main container z-index is set to 3; otherwise, it is set to 1
// This is a workaround to ensure dialogs are always on top of the header
export function useDialogObserver(targetSelector = MAIN_CONTAINER_SELECTOR) {
  const mainContainerElement = ref<HTMLElement | null>(null);
  const targetElement = ref<HTMLElement | null>(null);

  onMounted(() => {
    mainContainerElement.value = document.querySelector(MAIN_CONTAINER_SELECTOR);
    targetElement.value = document.querySelector(targetSelector);

    if (!targetElement.value) {
      console.error(`[useDialogObserver]: Target element "${targetSelector}" not found for dialog observer`);
    }    
    // Also check after next tick to catch modals that render asynchronously
    nextTick(() => {
      updateMainContainerZIndex();
    });
  });

  function updateMainContainerZIndex(): void {
    if (!mainContainerElement.value) return;

    // Check if any modal/dialog elements exist in the DOM
    const hasModals = MODAL_SELECTORS.some((selector) => document.querySelector(selector) !== null);

    mainContainerElement.value.style.zIndex = hasModals
      ? MAIN_CONTAINER_Z_INDEX_OVERLAP
      : MAIN_CONTAINER_Z_INDEX_INITIAL;
  }

  function isModalElement(node: Node): node is HTMLElement {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;

    const element = node as HTMLElement;

    // Check CSS class selectors
    const hasModalClass = MODAL_SELECTORS.some((selector) => {
      const className = selector.substring(1); // Remove the '.' prefix
      return element.classList && element.classList.contains(className);
    });

    return hasModalClass;
  }

  function hasModalInNodes(nodes: NodeList): boolean {
    return Array.from(nodes).some((node: Node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;

      const element = node as Element;

      // Check if the node itself is a modal element
      if (isModalElement(element)) return true;

      // Check if the element contains any modal descendants
      return MODAL_SELECTORS.some((selector) => element.querySelectorAll(selector).length > 0);
    });
  }

  useMutationObserver(
    targetElement,
    (mutations: MutationRecord[]) => {
      let hasDialogMutation = false;
      // Check if any mutations involve dialog elements
      mutations.forEach((mutation: MutationRecord) => {
        if (mutation.type === 'childList') {
          // Check added or removed nodes for modal elements
          if (hasModalInNodes(mutation.addedNodes) || hasModalInNodes(mutation.removedNodes)) {
            hasDialogMutation = true;
          }
        }
      });

      // Only update z-index if dialog-related mutations occurred
      if (hasDialogMutation) {
        updateMainContainerZIndex();
      }
    },
    {
      childList: true,
      subtree: true,
    },
  );
}
